import React from 'react';
import { Image } from '../../../../components/image';
import { RoccoLink } from '../../../../components/link/RoccoLink';
import { BackgroundVideo } from './BackgroundVideo';
import { MagazineSubscribeForm } from './MagazineSubscribeForm';
import type { FooterMagazineTile, FooterVideoData } from './types';
import type { SubscriptionFetcher } from '../EmailSubscriptionForm';

interface MagazineSectionProps {
    magazines: FooterMagazineTile[];
    /** Action URL for subscription form */
    subscriptionAction?: string;
    /** Fetcher for subscription form */
    subscriptionFetcher?: SubscriptionFetcher;
    /** Optional video data for background video */
    videoData?: FooterVideoData | null;
    /** Fallback background image URL when video is absent */
    fallbackImageSrc?: string;
}

/**
 * Magazine showcase section for the new footer design.
 * Displays "Magazine quality content in your inbox" heading with 3 magazine covers.
 */
export const MagazineSection: React.FC<MagazineSectionProps> = ({
    magazines,
    subscriptionAction,
    subscriptionFetcher,
    videoData,
    fallbackImageSrc,
}) => {
    // Don't render section if no magazines available
    if (magazines.length === 0) {
        return null;
    }

    // Handle edge cases: fewer than 3 magazines
    const leftMagazine = magazines[0];
    const centerMagazine = magazines[1] || magazines[0];
    const rightMagazine = magazines[2] || magazines[1] || magazines[0];

    return (
        <section className="relative overflow-hidden bg-[#212121] pb-22 pt-24">
            {/* Background video or fallback image — clipped above bottom padding on QHD+ to prevent subpixel bleed */}
            <div className="absolute inset-0 bottom-22">
                {videoData ? (
                    <BackgroundVideo videoData={videoData} />
                ) : fallbackImageSrc ? (
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${fallbackImageSrc})` }}
                        aria-hidden="true"
                    />
                ) : null}
            </div>
            {/* Top gradient overlay for text readability */}
            {(videoData || fallbackImageSrc) && (
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#212121]/40 via-transparent to-transparent" />
            )}

            {/* Bottom gradient fade - starts at mid-section, reaches solid #212121 by mid-magazine */}
            <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[65%]"
                style={{
                    background:
                        'linear-gradient(to bottom, transparent 0%, #212121 50%)',
                }}
            />

            <div className="relative z-10 mx-auto max-w-7xl px-12">
                {/* Heading */}
                <h2 className="mb-9 mt-6 text-center text-5xl font-normal leading-[98.75%] -tracking-[0.06em] text-white md:text-6xl">
                    Magazine quality content
                    <br />
                    in your inbox
                </h2>

                {/* Subscribe form */}
                {subscriptionAction && subscriptionFetcher && (
                    <div className="mb-9 flex justify-center">
                        <MagazineSubscribeForm
                            action={subscriptionAction}
                            fetcher={subscriptionFetcher}
                        />
                    </div>
                )}

                {/* Free Publications text */}
                <p className="mb-18 text-center text-[1.3rem] text-white/80">
                    Free Publications
                </p>
            </div>

            {/* Magazine covers - positioned with side images at page edges */}
            <div className="relative flex min-h-[40rem] items-end justify-center lg:min-h-[60rem]">
                {/* Left magazine - tilted left, positioned at left edge */}
                {leftMagazine && (
                    <RoccoLink
                        to={leftMagazine.link}
                        className="absolute bottom-0 left-0 hidden -rotate-6 transform transition-transform hover:scale-105 md:block"
                    >
                        <div className="relative h-[28rem] w-[16.8rem] border-[14px] border-white bg-white shadow-2xl lg:h-[41.375rem] lg:w-[24.8rem]">
                            <Image
                                src={leftMagazine.imageSrc}
                                alt={leftMagazine.title}
                                fill={true}
                                fit="cover"
                                quality={90}
                                sizes="(max-width: 1023px) 269px, 397px"
                            />
                        </div>
                    </RoccoLink>
                )}

                {/* Center magazine - largest, on top, triple border */}
                {centerMagazine && (
                    <RoccoLink
                        to={centerMagazine.link}
                        className="z-10 transform transition-transform hover:scale-105"
                    >
                        <div className="relative h-[21.5rem] w-[12.9rem] border-[14px] border-white bg-white shadow-2xl md:h-[41.375rem] md:w-[24.8rem] lg:h-[57.5rem] lg:w-[34.5rem]">
                            <Image
                                src={centerMagazine.imageSrc}
                                alt={centerMagazine.title}
                                fill={true}
                                fit="cover"
                                quality={90}
                                sizes="(max-width: 767px) 206px, (max-width: 1023px) 397px, 552px"
                            />
                        </div>
                    </RoccoLink>
                )}

                {/* Right magazine - tilted right, positioned at right edge */}
                {rightMagazine && magazines.length > 1 && (
                    <RoccoLink
                        to={rightMagazine.link}
                        className="absolute bottom-0 right-0 hidden rotate-6 transform transition-transform hover:scale-105 md:block"
                    >
                        <div className="relative h-[28rem] w-[16.8rem] border-[14px] border-white bg-white shadow-2xl lg:h-[41.375rem] lg:w-[24.8rem]">
                            <Image
                                src={rightMagazine.imageSrc}
                                alt={rightMagazine.title}
                                fill={true}
                                fit="cover"
                                quality={90}
                                sizes="(max-width: 1023px) 269px, 397px"
                            />
                        </div>
                    </RoccoLink>
                )}
            </div>
        </section>
    );
};
